import {
  Button,
  cn,
  formatDate as formatUiDate,
  ResponsiveTable,
  useTranslation,
} from '@hbs-us/ui';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { useMoneyFormatters } from '../_money/useMoneyFormatters.js';
import { MoreMenu } from '../_ops/MoreMenu.js';
import type {
  MonthlyBillInput,
  MonthlyBillListItem,
  MonthlyBillsResponse,
} from './contractsMirror.js';
import type { MonthlyBillsSearch } from './searchParams.js';
import {
  useArchiveBillMutation,
  useCreateBillMutation,
  useMarkPaidMutation,
  useRestoreBillMutation,
  useUpdateBillMutation,
} from './useMonthlyBillsQuery.js';

/**
 * The faithful 1:1 monthly-bills list port (UI-SPEC Recurring cluster) on the locked Velocity brand.
 * Page wrapper + header (title, subtitle, the single "+ New Bill" CTA) + a month filter + a totals
 * strip (scheduled-this-month + next-30-days sums + active count) + the bills table with per-row
 * inline "Mark paid", "Edit", "Archive"/"Restore" actions and an archived-visibility toggle — or the
 * empty state. The add/edit drawer is a focus-trapped modal.
 *
 * The coarse endpoint (22-12/22-25) owns the next-due + paid-badge + scheduled + next-30-days
 * computation; this view only renders the returned payload + fires the mutations. Every color is a
 * locked `@hbs-us/ui` token (no hex, BRAND-06). The financials.edit gate is enforced server-side.
 */

const INPUT_CLASS =
  'h-11 w-full rounded-md border border-input bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const LABEL_CLASS = 'mb-1 block font-medium text-foreground text-sm';

export interface MonthlyBillsViewProps {
  data: MonthlyBillsResponse;
  search: MonthlyBillsSearch;
}

export function MonthlyBillsView({ data, search }: MonthlyBillsViewProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const fmt = useMoneyFormatters();
  const navigate = useNavigate();
  const markPaid = useMarkPaidMutation();
  const archiveBill = useArchiveBillMutation();
  const restoreBill = useRestoreBillMutation();

  const month = data.month ?? search.month ?? '';
  const allBills = data.bills ?? [];
  const scheduledThisMonth = Number(data.summary?.scheduledThisMonth ?? 0);
  const next30DaysSum = Number(data.summary?.next30DaysSum ?? 0);
  const activeCount = Number(data.summary?.activeCount ?? 0);
  const archivedCount = allBills.filter((b) => b.archivedAt).length;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingBill, setEditingBill] = useState<MonthlyBillListItem | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  // Archived rows are hidden by default; the toggle reveals them (mirrors the fleet showArchived
  // pattern). No refetch needed — the coarse GET already returns archived rows.
  const visibleBills = showArchived ? allBills : allBills.filter((b) => !b.archivedAt);
  const hasData = visibleBills.length > 0;

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold text-3xl text-foreground">
            {t('monthlyBills:title')}
          </h1>
          <p className="mt-1 max-w-2xl text-muted-foreground text-sm">
            {t('monthlyBills:subtitle')}
          </p>
        </div>
        <Button onClick={() => setDrawerOpen(true)}>{t('monthlyBills:actions.newBill')}</Button>
      </header>

      <section
        aria-label={t('monthlyBills:totals.regionLabel')}
        className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3"
      >
        <div className="rounded-md border border-border bg-card p-6">
          <p className="text-muted-foreground text-sm">
            {t('monthlyBills:totals.scheduledThisMonth')}
          </p>
          <p className="mt-1 font-display font-semibold text-2xl text-foreground">
            {fmt.formatCurrency(scheduledThisMonth)}
          </p>
        </div>
        <div className="rounded-md border border-border bg-card p-6">
          <p className="text-muted-foreground text-sm">{t('monthlyBills:totals.next30Days')}</p>
          <p className="mt-1 font-display font-semibold text-2xl text-foreground">
            {fmt.formatCurrency(next30DaysSum)}
          </p>
        </div>
        <div className="rounded-md border border-border bg-card p-6">
          <p className="text-muted-foreground text-sm">{t('monthlyBills:totals.activeBills')}</p>
          <p className="mt-1 font-display font-semibold text-2xl text-foreground">{activeCount}</p>
        </div>
      </section>

      <section
        aria-label={t('monthlyBills:filter.regionLabel')}
        className="mb-4 flex flex-wrap items-end gap-3 rounded-md border border-border bg-card p-6"
      >
        <div>
          <label htmlFor="month-filter" className={LABEL_CLASS}>
            {t('monthlyBills:filter.month')}
          </label>
          <input
            id="month-filter"
            type="month"
            defaultValue={month}
            onChange={(event) => {
              const value = event.target.value;
              void navigate({
                to: '/monthly-bills',
                search: value ? { month: value } : {},
              });
            }}
            className={INPUT_CLASS}
          />
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={() => setShowArchived((prev) => !prev)}
          aria-pressed={showArchived}
        >
          {showArchived
            ? t('monthlyBills:filter.hideArchived')
            : t('monthlyBills:filter.showArchived', { count: archivedCount })}
        </Button>
      </section>

      {hasData ? (
        <BillsTable
          bills={visibleBills}
          month={month}
          fmt={fmt}
          locale={locale}
          onMarkPaid={(billId) => markPaid.mutate({ billId, month })}
          markPaidPending={markPaid.isPending}
          onEdit={(bill) => setEditingBill(bill)}
          onArchive={(billId) => archiveBill.mutate({ billId })}
          onRestore={(billId) => restoreBill.mutate({ billId })}
          mutating={archiveBill.isPending || restoreBill.isPending}
        />
      ) : (
        <div className="rounded-md border border-border bg-card p-16 text-center">
          <h2 className="font-display font-semibold text-foreground text-xl">
            {showArchived
              ? t('monthlyBills:empty.archivedHeading')
              : t('monthlyBills:empty.heading')}
          </h2>
          <p className="mx-auto mt-2 max-w-sm text-muted-foreground text-sm">
            {showArchived ? t('monthlyBills:empty.archivedBody') : t('monthlyBills:empty.body')}
          </p>
        </div>
      )}

      <BillDrawer mode="create" open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <BillDrawer
        mode="edit"
        open={editingBill !== null}
        bill={editingBill}
        onClose={() => setEditingBill(null)}
      />
    </div>
  );
}

function BillsTable({
  bills,
  month,
  fmt,
  locale,
  onMarkPaid,
  markPaidPending,
  onEdit,
  onArchive,
  onRestore,
  mutating,
}: {
  bills: MonthlyBillListItem[];
  month: string;
  fmt: ReturnType<typeof useMoneyFormatters>;
  locale: string;
  onMarkPaid: (billId: number) => void;
  markPaidPending: boolean;
  onEdit: (bill: MonthlyBillListItem) => void;
  onArchive: (billId: number) => void;
  onRestore: (billId: number) => void;
  mutating: boolean;
}) {
  const { t } = useTranslation();
  return (
    <ResponsiveTable className="rounded-md border border-border bg-card">
      <table className="w-full text-left text-sm">
        <thead className="border-border border-b text-muted-foreground">
          <tr>
            <th className="px-4 py-3 font-medium">{t('monthlyBills:table.name')}</th>
            <th className="px-4 py-3 font-medium">{t('monthlyBills:table.vendor')}</th>
            <th className="px-4 py-3 font-medium">{t('monthlyBills:table.amount')}</th>
            <th className="px-4 py-3 font-medium">{t('monthlyBills:table.nextDue')}</th>
            <th className="px-4 py-3 font-medium">{t('monthlyBills:table.status')}</th>
            <th className="px-4 py-3 text-right font-medium">{t('monthlyBills:table.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {bills.map((bill) => {
            const archived = Boolean(bill.archivedAt);
            return (
              <tr key={bill.id} className="border-border border-b last:border-0 hover:bg-accent/40">
                <td className="px-4 py-3 font-medium text-foreground">{bill.name}</td>
                <td className="px-4 py-3 text-muted-foreground">
                  {bill.vendor ?? t('monthlyBills:table.noVendor')}
                </td>
                <td className="px-4 py-3 font-mono text-foreground">
                  {fmt.formatCurrency(bill.amount)}
                </td>
                <td className="px-4 py-3 font-mono text-muted-foreground">
                  {bill.nextDueDate
                    ? formatUiDate(bill.nextDueDate, locale, { dateStyle: 'medium' })
                    : t('monthlyBills:table.inactive')}
                </td>
                <td className="px-4 py-3">
                  {archived ? (
                    <span className="inline-flex items-center rounded-md border border-border bg-background px-2 py-1 font-medium text-muted-foreground text-xs">
                      {t('monthlyBills:status.archived')}
                    </span>
                  ) : bill.paid ? (
                    <span
                      className={cn(
                        'inline-flex items-center rounded-md bg-secondary px-2 py-1 font-medium text-secondary-foreground text-xs',
                      )}
                    >
                      {t('monthlyBills:status.paid', { month })}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onMarkPaid(bill.id)}
                      disabled={markPaidPending}
                      className="inline-flex h-11 items-center rounded-md border border-border bg-card px-3 font-medium text-foreground text-sm transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-60"
                    >
                      {t('monthlyBills:actions.markPaid')}
                    </button>
                  )}
                </td>
                <td className="px-4 py-3">
                  {/* The shared row-action menu (UI-SPEC §4 "Inline row actions"), same as the
                      employees + users tables. The bill NAME belongs on the trigger's accessible
                      name and nowhere else: it is what tells a screen-reader user which of a list
                      of near-identical rows this menu acts on, while a real name runs to a whole
                      clause ("Insurance, Auto-WorkersComp, Liability") and printing it inside every
                      visible label swamped the table. Menu items carry the verb alone. */}
                  <div className="flex justify-end">
                    <MoreMenu
                      label={t('monthlyBills:actions.menuLabel', { name: bill.name })}
                      items={
                        archived
                          ? [
                              {
                                key: 'restore',
                                label: t('monthlyBills:actions.restore'),
                                disabled: mutating,
                                onSelect: () => onRestore(bill.id),
                              },
                            ]
                          : [
                              {
                                key: 'edit',
                                label: t('monthlyBills:actions.edit'),
                                onSelect: () => onEdit(bill),
                              },
                              {
                                key: 'archive',
                                label: t('monthlyBills:actions.archive'),
                                disabled: mutating,
                                onSelect: () => onArchive(bill.id),
                              },
                            ]
                      }
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </ResponsiveTable>
  );
}

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])';

/**
 * The add/edit bill drawer — a focus-trapped modal. In `create` mode it POSTs a new bill; in `edit`
 * mode it prefills from the row and PATCHes via useUpdateBillMutation (the financials.edit gate holds
 * server-side). Active state + end date are carried through on edit so they are never wiped.
 */
function BillDrawer({
  mode,
  open,
  bill,
  onClose,
}: {
  mode: 'create' | 'edit';
  open: boolean;
  bill?: MonthlyBillListItem | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const createMutation = useCreateBillMutation();
  const updateMutation = useUpdateBillMutation();
  const headingId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isEdit = mode === 'edit';

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const node = dialogRef.current;
      if (!node) return;
      const focusable = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    setError(null);
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    return () => {
      previouslyFocused.current?.focus();
    };
  }, [open]);

  if (!open) return null;
  if (isEdit && !bill) return null;

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    const form = new FormData(event.currentTarget);
    const input: MonthlyBillInput = {
      name: String(form.get('name') ?? '').trim(),
      vendor: String(form.get('vendor') ?? '').trim() || null,
      category: String(form.get('category') ?? '').trim() || null,
      amount: Number(form.get('amount')),
      due_day: Number(form.get('due_day')),
      effective_start_date: String(form.get('effective_start_date') ?? '').trim(),
    };

    if (isEdit && bill) {
      // Carry end date + active through so an edit never clears them (the list DTO has both).
      const endDate = String(form.get('end_date') ?? '').trim();
      updateMutation.mutate(
        {
          billId: bill.id,
          input: { ...input, end_date: endDate || null, active: bill.active },
        },
        {
          onSuccess: () => onClose(),
          onError: (err) => setError(err.message),
        },
      );
      return;
    }

    createMutation.mutate(input, {
      onSuccess: () => onClose(),
      onError: (err) => setError(err.message),
    });
  };

  const pending = isEdit ? updateMutation.isPending : createMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-foreground/40">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        onKeyDown={handleKeyDown}
        className="h-full w-full max-w-md overflow-y-auto border-border border-l bg-card p-6 shadow-lg"
      >
        <h2 id={headingId} className="font-display font-semibold text-foreground text-xl">
          {isEdit ? t('monthlyBills:form.editHeading') : t('monthlyBills:form.createHeading')}
        </h2>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          {error ? (
            <p role="alert" className="rounded-md bg-destructive/10 p-3 text-destructive text-sm">
              {error}
            </p>
          ) : null}

          <div>
            <label htmlFor="bill-name" className={LABEL_CLASS}>
              {t('monthlyBills:form.name')}
            </label>
            <input
              id="bill-name"
              name="name"
              required
              defaultValue={bill?.name ?? ''}
              className={INPUT_CLASS}
            />
          </div>
          <div>
            <label htmlFor="bill-vendor" className={LABEL_CLASS}>
              {t('monthlyBills:form.vendor')}
            </label>
            <input
              id="bill-vendor"
              name="vendor"
              defaultValue={bill?.vendor ?? ''}
              className={INPUT_CLASS}
            />
          </div>
          <div>
            <label htmlFor="bill-category" className={LABEL_CLASS}>
              {t('monthlyBills:form.category')}
            </label>
            <input
              id="bill-category"
              name="category"
              defaultValue={bill?.category ?? ''}
              className={INPUT_CLASS}
            />
          </div>
          <div>
            <label htmlFor="bill-amount" className={LABEL_CLASS}>
              {t('monthlyBills:form.amount')}
            </label>
            <input
              id="bill-amount"
              name="amount"
              type="number"
              min="0"
              step="0.01"
              required
              inputMode="decimal"
              defaultValue={bill ? String(bill.amount) : ''}
              className={INPUT_CLASS}
            />
          </div>
          <div>
            <label htmlFor="bill-due-day" className={LABEL_CLASS}>
              {t('monthlyBills:form.dueDay')}
            </label>
            <input
              id="bill-due-day"
              name="due_day"
              type="number"
              min="1"
              max="31"
              required
              inputMode="numeric"
              defaultValue={bill ? String(bill.dueDay) : ''}
              className={INPUT_CLASS}
            />
          </div>
          <div>
            <label htmlFor="bill-start" className={LABEL_CLASS}>
              {t('monthlyBills:form.effectiveStartDate')}
            </label>
            <input
              id="bill-start"
              name="effective_start_date"
              type="date"
              required
              defaultValue={bill?.effectiveStartDate ?? ''}
              className={INPUT_CLASS}
            />
          </div>
          {isEdit ? (
            <div>
              <label htmlFor="bill-end" className={LABEL_CLASS}>
                {t('monthlyBills:form.endDate')}
              </label>
              <input
                id="bill-end"
                name="end_date"
                type="date"
                defaultValue={bill?.endDate ?? ''}
                className={INPUT_CLASS}
              />
            </div>
          ) : null}

          <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('monthlyBills:form.cancel')}
            </Button>
            <Button type="submit" disabled={pending}>
              {isEdit ? t('monthlyBills:form.submitEdit') : t('monthlyBills:form.submitCreate')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
